// Sessions tab — list every saved chat/coding session across users.
//
// Note: this is the server's "saved conversation session" concept,
// not active browser login sessions. The design prototype's mockup
// implies HTTP sessions (revoke device etc.) but the server doesn't
// expose those today. Browser-session management is a Phase 2 ask.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type AdminChatSession } from "../../lib/rpc";

interface SessionsTabProps {
  notify: (msg: string) => void;
}

export function AdminSessionsTab({ notify }: SessionsTabProps) {
  const [sessions, setSessions] = useState<AdminChatSession[] | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.adminListSessions()
      .then((s) => { if (!cancelled) setSessions(s); })
      .catch((e: any) => { if (!cancelled) { notify(e?.message || "Couldn't load sessions"); setSessions([]); } });
    return () => { cancelled = true; };
  }, [notify]);

  const visible = useMemo(() => {
    if (!sessions) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      s.userId.toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.preview || "").toLowerCase().includes(q),
    );
  }, [sessions, filter]);

  return (
    <div className="admin-card">
      <h3>Sessions</h3>
      <p className="admin-card-sub">
        Saved conversations across all users — chat + coding. Click a row to copy its id.
      </p>
      <div className="admin-audit-filters">
        <input
          className="input"
          placeholder="filter by user, title, or preview…"
          value={filter}
          onInput={(e) => setFilter((e.currentTarget as HTMLInputElement).value)}
        />
      </div>

      {visible === null ? (
        <p className="admin-loading">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="admin-empty">No sessions match.</p>
      ) : (
        <div className="admin-session-list">
          {visible.map((s) => (
            <button
              key={`${s.userId}:${s.id}`}
              className="admin-session-row"
              onClick={() => {
                try { navigator.clipboard.writeText(s.id); notify(`Copied ${s.id}`); } catch {}
              }}
              type="button"
              title="Copy session id"
            >
              <span className="admin-session-icon" data-kind={s.kind}>
                <Icon name={s.kind === "coding" ? "terminal" : "chat"} size={14} />
              </span>
              <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <div className="admin-session-title">
                  {s.name || "Untitled"}
                  <span className="admin-pill" style={{ marginLeft: 6 }}>{s.kind}</span>
                </div>
                <div className="admin-session-meta">
                  <span className="admin-user-mono">{s.userId}</span> · {s.messageCount} msg · {s.updatedAt.slice(0, 19).replace("T", " ")}
                </div>
                {s.preview && <div className="admin-session-preview">{s.preview}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
